import React, { useState, useEffect } from 'react';
import { BrowserRouter, HashRouter, Route, Routes, useParams, Navigate } from 'react-router-dom';
import AuthService from './components/AuthService/AuthService';

import SignUp from './components/Signup/Signup';
import Login from './components/Login/Login';
import Footer from './components/Footer/footer';
import Navbar from './components/Navbar/Navbar';
import Home from './components/Home/Home';
import Dashboard from './components/Dashboard/Dashboard';
import Contact from './components/Contact/Contact';
import PasswordResetRequest from './components/PasswordResetRequest/PasswordResetRequest';
import PasswordResetVerify from './components/PasswordResetVerify/PasswordResetVerify';
import PasswordReset from './components/PasswordReset/PasswordReset';
import HeroSection from './components/HeroSection/HeroSection';
import ComingSoonAppSection from './components/ComingSoonAppSection/ComingSoonAppSection';
import FeaturesSection from './components/FeaturesSection/FeaturesSection';
import HowItWorks from './components/HowItWorks/HowItWorks';
import AmbassadorPromoSection from './components/AmbassadorPromoSection/AmbassadorPromoSection';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import AboutUs from './components/AboutUs/AboutUs';
import AmbassadorProgram from './components/AmbassadorPromogram/AmbassadorProgram';
import AmbassadorRegister from './components/AmbassadorRegister/AmbassadorRegister';
import OtpVerification from './components/OtpVerification/OtpVerification';
import PharmacyProfile from './components/PharmacyProfile/PharmacyProfile';
import PharmacyVerificationForm from './components/PharmacyVerificationForm/PharmacyVerificationForm';
import UpdatePharmacyProfile from './components/UpdatePharmacyProfile/UpdatePharmacyProfile';
import ManagePharmacyBranch from './components/ManagePharmacyBranch/ManagePharmacyBranch';
import Sidebar from './components/Sidebar/Sidebar';
import CreatePharmacyBranch from './components/CreatePharmacyBranch/CreatePharmacyBranch';
import PharmacyBranches from './components/PharmacyBranches/PharmacyBranches';
import ManageInventory from './components/ManageInventory/ManageInventory';
import AddInventoryManually from './components/AddInventoryManually/AddInventoryManually';
import UploadInventoryCSV from './components/UploadInventoryCSV/UploadInventoryCSV';
import InventoryItem from './components/InventoryItem/InventoryItem';
import UpdateInventoryCSV from './components/UpdateInventoryCSV/UpdateInventoryCSV';
import UpdateInventoryManually from './components/UpdateInventoryManually/UpdateInventoryManually';
import DrugAnalytics from './components/DrugAnalytics/DrugAnalytics';
import BranchAnalytics from './components/BranchAnalytics/BranchAnalytics';
import Analytics from './components/Analytics/Analytics';
import PharmacyBranchProfile from './components/PharmacyBranchProfile/PharmacyBranchProfile';
import UpdatePharmacyBranchProfile from './components/UpdatePharmacyBranchProfile/UpdatePharmacyBranchProfile';
import PharmacyBranchPasswordReset from './components/PharmacyBranchPasswordReset/PharmacyBranchPasswordReset';
import UpdateBatch from './components/UpdateBatch/UpdateBatch';
import POS from './components/POS/POS';
import ReceiptModal from './components/ReceiptModal/ReceiptModal';
import SalesHistory from './components/SalesHistory/SalesHistory';
import StaffManagement from './components/StaffManagement/StaffManagement';
import PharmacyStaffprofile from './components/PharmacyStaffprofile/PharmacyStaffprofile';
import Subscription from './components/Subscription/Subscription';
import PaystackCallback from './components/PaystackCallback/PaystackCallback';
import Devices from './components/Devices/Devices';

const isElectron = window.navigator.userAgent.includes('Electron');
const IS_DESKTOP = process.env.REACT_APP_ENV === 'desktop';
const Router = isElectron ? HashRouter : BrowserRouter;

// ── Backend readiness check (desktop only) ──
// Polls /health every second until backend responds with 200.
// No max attempt limit — keeps retrying until backend is ready.
// This is the permanent fix for the infinite loading screen:
// instead of Electron guessing when the backend is ready,
// React itself waits and only renders the app when confirmed ready.
function useBackendReady() {
  // On web (non-desktop) — always ready, skip polling entirely
  const [ready, setReady] = useState(!IS_DESKTOP);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!IS_DESKTOP) return;

    let cancelled = false;
    let timer = null;

    const check = async () => {
      if (cancelled) return;
      try {
        const res = await fetch('http://127.0.0.1:8000/health', {
          signal: AbortSignal.timeout(3000), // 3s timeout per attempt
        });
        if (res.ok && !cancelled) {
          setReady(true);
        } else {
          scheduleRetry();
        }
      } catch {
        scheduleRetry();
      }
    };

    const scheduleRetry = () => {
      if (cancelled) return;
      setAttempts(a => a + 1);
      // Retry every 1 second — no limit, keeps trying until backend responds
      timer = setTimeout(check, 1000);
    };

    // Start first check immediately
    check();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return { ready, attempts };
}

// ── Home page wrapper ──
const HomeOrDashboard = () => {
  if (IS_DESKTOP && AuthService.isLoggedIn()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Home />;
};

function App() {
  const { ready, attempts } = useBackendReady();

  if (!ready) {
    // ── Status message cycles based on how long we've been waiting ──
    const statusMsg =
      attempts < 5  ? 'Starting MyApoti...'         :
      attempts < 15 ? 'Loading backend...'           :
      attempts < 30 ? 'Almost ready...'              :
                      'Taking longer than usual...';

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#1a1a2e',
        color: '#fff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        gap: '16px',
        userSelect: 'none',
      }}>
        {/* Logo */}
        <div style={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '4px' }}>
          My<span style={{ color: '#ffc107' }}>Apoti</span>
        </div>

        {/* Spinner */}
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTop: `3px solid ${attempts >= 30 ? '#ff9800' : '#ffc107'}`,
          borderRadius: '50%',
          animation: 'spin 0.9s linear infinite',
        }} />
        <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>

        {/* Status */}
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.05em' }}>
          {statusMsg}
        </p>

        {/* Retry button — shown after 30 seconds of waiting */}
        {attempts >= 30 && (
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '8px',
              padding: '0.6rem 1.5rem',
              background: '#ffc107',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            🔄 Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <Router>
      <div className="app-container">
        <Navbar />
        <div className="main-content">
          <Routes>
            <Route path="/" element={<HomeOrDashboard />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/paystackcallback" element={<PaystackCallback />} />
            <Route path="/login" element={<Login />} />
            <Route path="/PharmacyStaffprofile/:staffId" element={<PharmacyStaffprofile />} />
            <Route path="/pos" element={<POS />} />
            <Route path="/Subscription" element={<Subscription />} />
            <Route path="/Analytics" element={<Analytics />} />
            <Route path="/UpdateBatch" element={<UpdateBatch />} />
            <Route path="/StaffManagement" element={<StaffManagement />} />
            <Route path="/SalesHistory" element={<SalesHistory />} />
            <Route path="/UpdateInventoryCSV" element={<UpdateInventoryCSV />} />
            <Route path="/ReceiptModal" element={<ReceiptModal />} />
            <Route path="/BranchAnalytics" element={<BranchAnalytics />} />
            <Route path="/DrugAnalytics" element={<DrugAnalytics />} />
            <Route path="/UpdateInventoryManually" element={<UpdateInventoryManually />} />
            <Route path="/PharmacyVerificationForm" element={<PharmacyVerificationForm />} />
            <Route path="/InventoryItem" element={<InventoryItem />} />
            <Route path="/AddInventoryManually" element={<AddInventoryManually />} />
            <Route path="/UploadInventoryCSV" element={<UploadInventoryCSV />} />
            <Route path="/PharmacyBranches" element={<PharmacyBranches />} />
            <Route path="/ManageInventory" element={<ManageInventory />} />
            <Route path="/Sidebar" element={<Sidebar />} />
            <Route path="/ManagePharmacyBranch" element={<ManagePharmacyBranch />} />
            <Route path="/UpdatePharmacyProfile" element={<UpdatePharmacyProfile />} />
            <Route path="/CreatePharmacyBranch" element={<CreatePharmacyBranch />} />
            <Route path="/PharmacyProfile" element={<PharmacyProfile />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/AboutUs" element={<AboutUs />} />
            <Route path="/AmbassadorProgram" element={<AmbassadorProgram />} />
            <Route path="/AmbassadorRegister" element={<AmbassadorRegister />} />
            <Route path="/OtpVerification" element={<OtpVerification />} />
            <Route path="/AmbassadorPromoSection" element={<AmbassadorPromoSection />} />
            <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
            <Route path="/HowItWorks" element={<HowItWorks />} />
            <Route path="/RegularSubscription" element={<FeaturesSection />} />
            <Route path="/HeroSection" element={<HeroSection />} />
            <Route path="/PasswordResetVerify" element={<PasswordResetVerify />} />
            <Route path="/ComingSoonAppSection" element={<ComingSoonAppSection />} />
            <Route path="/PasswordReset" element={<PasswordReset />} />
            <Route path="/PasswordResetRequest" element={<PasswordResetRequest />} />
            <Route path="/Contact" element={<Contact />} />
            <Route path="/Devices" element={<Devices />} />
            <Route path="/pharmacy/branch/:branchId" element={<BranchProfileWrapper />} />
            <Route path="/UpdatePharmacyBranchProfile/:branchId" element={<UpdatePharmacyBranchWrapper />} />
            <Route path="/PharmacyBranchPasswordReset/:branchId" element={<PharmacyBranchPasswordResetWrapper />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </Router>
  );
}

const BranchProfileWrapper = () => {
  const { branchId } = useParams();
  return <PharmacyBranchProfile branchId={branchId} />;
};

const UpdatePharmacyBranchWrapper = () => {
  const { branchId } = useParams();
  return <UpdatePharmacyBranchProfile branchId={branchId} />;
};

const PharmacyBranchPasswordResetWrapper = () => {
  const { branchId } = useParams();
  return <PharmacyBranchPasswordReset branchId={branchId} />;
};

export default App;